import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
	useFeatureDecorators,
	type FeatureDecorator,
} from './use-feature-decorators';
import {
	Megaphone,
	Workflow,
	Kanban,
	Activity,
	Plus,
	Home,
	CalendarDays,
	BriefcaseBusiness,
	CreditCard,
	FileText,
	Star,
	Pencil,
	Split,
	ChartLine,
	Zap,
	KeyRound,
	Pin,
	PinOff,
	LayoutGrid,
	Search,
	SlidersHorizontal,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { isMobileViewport, isTouchViewport } from '@/hooks/use-mobile';
import {
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePinnedFeatures } from './use-pinned-features';
import { useHiddenFeatures } from './use-hidden-features';

export interface FeatureItem {
	id: string;
	title: string;
	url: string;
	icon: React.ElementType;
	requiredProduct?: string;
	hiddenForResellers?: boolean;
}

export interface FeatureAuth {
	products?: string[];
	reseller?: string;
}

export interface FeatureCategory {
	title: string;
	icon: React.ElementType;
	items: FeatureItem[];
}

export const ALL_FEATURES: FeatureCategory[] = [
	{
		title: 'Outreach',
		icon: Megaphone,
		items: [
			{
				id: 'campaigns',
				title: 'Campaigns',
				url: '/campaigns',
				icon: Megaphone,
			},
			{
				id: 'sequences',
				title: 'Sequences',
				url: '/sequences',
				icon: Workflow,
			},
			{
				id: 'pipeline',
				title: 'Pipeline',
				url: '/pipeline',
				icon: Kanban,
			},
			{
				id: 'activity-feed',
				title: 'Activity Feed',
				url: '/activity-feed',
				icon: Activity,
			},
		],
	},
	{
		title: 'Payments',
		icon: CreditCard,
		items: [
			{
				id: 'payments',
				title: 'Recent Payments',
				url: '/payments',
				icon: CreditCard,
			},
		],
	},
	{
		title: 'Reputation',
		icon: Star,
		items: [
			{ id: 'reviews', title: 'Reviews', url: '/reviews', icon: Star },
			{
				id: 'leaderboard',
				title: 'Leaderboard',
				url: '/reviews/leaderboard',
				icon: Star,
			},
			{
				id: 'competitor',
				title: 'Competitor',
				url: '/reviews/competitor',
				icon: Star,
			},
		],
	},
	{
		title: 'Analytics',
		icon: ChartLine,
		items: [
			{
				id: 'messenger-analytics',
				title: 'Message Analytics',
				url: '/messenger-analytics',
				icon: ChartLine,
			},
		],
	},
	{
		title: 'Forms',
		icon: Pencil,
		items: [
			{
				id: 'forms',
				title: 'Customer Forms',
				url: '/forms',
				icon: Pencil,
			},
		],
	},
	{
		title: 'Automation',
		icon: Split,
		items: [
			{
				id: 'smart-replies',
				title: 'Smart Replies',
				url: '/automations/smart-replies',
				icon: Split,
			},
			{
				id: 'events-triggers',
				title: 'Events & Triggers',
				url: '/automations/message-automations',
				icon: Zap,
			},
			{
				id: 'zapier',
				title: 'Zapier',
				url: '/automations/zapier',
				icon: Zap,
			},
			{
				id: 'api',
				title: 'API',
				url: '/automations/api',
				icon: KeyRound,
				hiddenForResellers: true,
			},
		],
	},
	{
		title: 'Work',
		icon: BriefcaseBusiness,
		items: [
			{
				id: 'create',
				title: 'Create',
				url: '/work/create',
				icon: Plus,
			},
			{ id: 'today', title: 'Today', url: '/work/today', icon: Home },
			{
				id: 'calendar',
				title: 'Calendar',
				url: '/work/calendar',
				icon: CalendarDays,
			},
			{
				id: 'all-work',
				title: 'All Work',
				url: '/work/everything',
				icon: BriefcaseBusiness,
			},
			{
				id: 'quotes',
				title: 'Quotes',
				url: '/work/quotes',
				icon: FileText,
			},
			{
				id: 'jobs',
				title: 'Jobs',
				url: '/work/jobs',
				icon: BriefcaseBusiness,
			},
			{
				id: 'invoices',
				title: 'Invoices',
				url: '/work/invoices',
				icon: FileText,
			},
		],
	},
];

export function getAllFeatureItems(): FeatureItem[] {
	return ALL_FEATURES.flatMap((cat) => cat.items);
}

export function isFeatureAvailable(item: FeatureItem, auth?: FeatureAuth) {
	if (item.hiddenForResellers && auth?.reseller) return false;
	return (
		!item.requiredProduct ||
		(auth?.products ?? []).includes(item.requiredProduct)
	);
}

const DISCOVERY_KEY = 'sidebar-features-discovered';

type NavigatorState = { open: boolean; query: string; editing: boolean };
const INITIAL_NAVIGATOR_STATE: NavigatorState = { open: false, query: '', editing: false };
const NavigatorStateContext = createContext<
	[NavigatorState, React.Dispatch<React.SetStateAction<NavigatorState>>] | null
>(null);

/**
 * Keeps the More Features sheet's open state, search and edit mode above the
 * sidebar, which swaps between the phone drawer and the desktop sidebar (and
 * remounts FeatureNavigator) when a phone rotates across the md breakpoint.
 * The sheet lives in the drawer on phones, so the drawer reopens with it.
 */
export function FeatureNavigatorStateProvider({ children }: { children: React.ReactNode }) {
	const value = useState(INITIAL_NAVIGATOR_STATE);
	const { isMobile, setOpenMobile } = useSidebar();
	const sheetOpen = value[0].open;
	useEffect(() => {
		if (isMobile && sheetOpen) setOpenMobile(true);
		// Only when the size class changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobile]);
	return (
		<NavigatorStateContext.Provider value={value}>
			{children}
		</NavigatorStateContext.Provider>
	);
}

function useNavigatorState<K extends keyof NavigatorState>(
	key: K,
): [NavigatorState[K], React.Dispatch<React.SetStateAction<NavigatorState[K]>>] {
	const shared = useContext(NavigatorStateContext);
	const local = useState(INITIAL_NAVIGATOR_STATE[key]);
	if (!shared) return local;
	const [state, setState] = shared;
	return [
		state[key],
		(next) =>
			setState((prev) => ({
				...prev,
				[key]: typeof next === 'function' ? (next as (value: NavigatorState[K]) => NavigatorState[K])(prev[key]) : next,
			})),
	];
}

export function FeatureNavigator() {
	const navigate = useNavigate();
	const location = useLocation();
	const { state: sidebarState, isMobile } = useSidebar();
	const isCollapsed = sidebarState === 'collapsed';

	const auth = useSelector((state: { auth: FeatureAuth }) => state.auth);

	const [dialogOpen, setDialogOpen] = useNavigatorState('open');
	const [isEditing, setIsEditing] = useNavigatorState('editing');
	const [showDiscoveryHint, setShowDiscoveryHint] = useState(false);
	const [query, setQuery] = useNavigatorState('query');
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const searchInputRef = useRef<HTMLInputElement>(null);
	// Phones swap Edit for Done in the sheet header; focus follows the swap.
	const phoneEditButtonRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		const dismissed = localStorage.getItem(DISCOVERY_KEY);
		if (!dismissed) setShowDiscoveryHint(true);
	}, []);

	const dismissDiscoveryHint = () => {
		setShowDiscoveryHint(false);
		localStorage.setItem(DISCOVERY_KEY, 'true');
	};

	const { pinnedIds, togglePin, unpin } = usePinnedFeatures();
	const { hiddenIds, setHidden } = useHiddenFeatures();

	const decorators = useFeatureDecorators();
	const attentionCount = useMemo(() => {
		return Object.entries(decorators).reduce((sum, [id, d]) => {
			if (pinnedIds.includes(id) || hiddenIds.includes(id)) return sum;
			return sum + (d.count ?? 0);
		}, 0);
	}, [decorators, pinnedIds, hiddenIds]);

	const filteredCategories = useMemo(() => {
		const search = query.trim().toLowerCase();
		return ALL_FEATURES.map((category) => ({
			...category,
			items: category.items.filter(
				(item) =>
					isFeatureAvailable(item, auth) &&
					(isEditing || !hiddenIds.includes(item.id)) &&
					(!search || item.title.toLowerCase().includes(search)),
			),
		})).filter((category) => category.items.length > 0);
	}, [auth, query, isEditing, hiddenIds]);

	const handleSetHidden = (featureIds: string[], hidden: boolean) => {
		setHidden(featureIds, hidden);
		if (hidden)
			featureIds.filter((id) => pinnedIds.includes(id)).forEach(unpin);
	};

	const toggleEditing = () => {
		setIsEditing((prev) => !prev);
		setHighlightedIndex(0);
		// On touch screens focusing the field would throw the keyboard over the list.
		if (isMobileViewport()) requestAnimationFrame(() => phoneEditButtonRef.current?.focus());
		else if (!isTouchViewport()) searchInputRef.current?.focus();
	};

	const flatItems = useMemo(
		() => filteredCategories.flatMap((category) => category.items),
		[filteredCategories],
	);

	const handleNavigate = (url: string) => {
		navigate(url);
		setDialogOpen(false);
	};

	const handleOpenChange = (open: boolean) => {
		setDialogOpen(open);
		if (!open) {
			setQuery('');
			setHighlightedIndex(0);
			setIsEditing(false);
		}
	};

	// Stop Enter/arrows from reaching document-level listeners (e.g. the chat
	// composer's send-on-enter) so searching can never send a message.
	const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
			e.preventDefault();
			e.stopPropagation();
			if (!flatItems.length) return;
			const offset = e.key === 'ArrowDown' ? 1 : -1;
			setHighlightedIndex(
				(prev) => (prev + offset + flatItems.length) % flatItems.length,
			);
			return;
		}

		if (e.key === 'Enter') {
			e.preventDefault();
			e.stopPropagation();
			const item = flatItems[highlightedIndex];
			if (!item) return;
			if (isEditing) {
				handleSetHidden([item.id], !hiddenIds.includes(item.id));
			} else {
				handleNavigate(item.url);
			}
		}
	};

	const trigger = isCollapsed ? (
		<Tooltip>
			<TooltipTrigger asChild>
				<DialogTrigger asChild>
					<button
						className="relative mx-auto flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
						data-onboarding="more-features" onClick={dismissDiscoveryHint}
					>
						<LayoutGrid className="size-4" />
						{showDiscoveryHint && (
							<span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-primary animate-pulse" />
						)}
						{!showDiscoveryHint && attentionCount > 0 && (
							<span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary text-[8px] font-bold text-primary-foreground px-0.5">
								{attentionCount}
							</span>
						)}
					</button>
				</DialogTrigger>
			</TooltipTrigger>
			<TooltipContent side="right">More Features</TooltipContent>
		</Tooltip>
	) : (
		<SidebarMenuItem>
			<DialogTrigger asChild>
				<SidebarMenuButton
					tooltip="More Features"
					data-onboarding="more-features" onClick={dismissDiscoveryHint}
				>
					<LayoutGrid className="size-4" />
					<div className="flex items-center justify-between w-full">
						<span>More Features</span>
						{showDiscoveryHint && (
							<span className="relative flex h-3 w-3">
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
								<span className="relative inline-flex h-3 w-3 rounded-full bg-primary" />
							</span>
						)}
						{!showDiscoveryHint && attentionCount > 0 && (
							<span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground px-1">
								{attentionCount}
							</span>
						)}
						{!showDiscoveryHint && attentionCount === 0 && (
							<Plus className="size-4 text-muted-foreground" />
						)}
					</div>
				</SidebarMenuButton>
			</DialogTrigger>
		</SidebarMenuItem>
	);

	return (
		<Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
			{trigger}
			<DialogContent
				className="top-[8vh] flex w-[calc(100%-2rem)] max-w-4xl translate-y-0 flex-col gap-0 overflow-hidden rounded-lg p-0 md:data-[state=closed]:!slide-out-to-top-0 md:data-[state=open]:!slide-in-from-top-0 max-md:h-[calc(var(--app-height)-var(--safe-top)-1.5rem)] max-md:overflow-hidden max-md:pb-0 max-md:pt-5 max-md:[&>button:last-child]:hidden md:max-xl:max-h-[calc(var(--app-height)-8vh-1rem)] md:max-xl:overflow-hidden"
				onOpenAutoFocus={(e) => {
					e.preventDefault();
					// Touch screens (phones in either orientation, iPads): focus the
					// sheet, not the field, so the keyboard doesn't cover the list.
					if (isTouchViewport()) (e.currentTarget as HTMLElement | null)?.focus();
					else searchInputRef.current?.focus();
				}}
			>
				{/* Phone sheet header, as on iOS: Edit, title, Done. While editing,
				    Done ends editing and the sheet stays open. */}
				<div className="grid h-11 shrink-0 grid-cols-[1fr_auto_1fr] items-center px-1.5 md:hidden">
					{isEditing ? (
						<span />
					) : (
						<button
							ref={phoneEditButtonRef}
							type="button"
							onClick={toggleEditing}
							className="inline-flex h-11 items-center justify-self-start whitespace-nowrap rounded-lg px-2.5 text-[17px] text-primary active:opacity-50"
						>
							Edit
						</button>
					)}
					<span aria-hidden className="text-[17px] font-semibold">
						Features
					</span>
					{isEditing ? (
						<button
							ref={phoneEditButtonRef}
							type="button"
							onClick={toggleEditing}
							aria-label="Done editing"
							className="inline-flex h-11 items-center justify-self-end whitespace-nowrap rounded-lg px-2.5 text-[17px] font-semibold text-primary active:opacity-50"
						>
							Done
						</button>
					) : (
						<DialogClose className="inline-flex h-11 items-center justify-self-end whitespace-nowrap rounded-lg px-2.5 text-[17px] font-semibold text-primary active:opacity-50">
							Done
						</DialogClose>
					)}
				</div>
				<DialogTitle className="sr-only">More Features</DialogTitle>
				<DialogDescription className="sr-only">
					Search or pick a feature to open it. Pin the ones you use most to the
					sidebar, or choose Edit to hide the ones you don't use.
				</DialogDescription>
				<div className="flex items-center gap-2 border-b py-3 pl-4 pr-12 max-md:mx-4 max-md:mb-2 max-md:mt-1 max-md:h-10 max-md:shrink-0 max-md:rounded-[10px] max-md:border-0 max-md:bg-muted max-md:px-3 max-md:py-0">
					<Search className="size-4 shrink-0 text-muted-foreground" />
					<input
						ref={searchInputRef}
						type="search"
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setHighlightedIndex(0);
						}}
						onKeyDown={handleSearchKeyDown}
						placeholder="Search features..."
						aria-label="Search features"
						autoCapitalize="none"
						autoCorrect="off"
						autoComplete="off"
						enterKeyHint="go"
						className="w-full bg-transparent text-base outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
					/>
					<button
						type="button"
						onClick={toggleEditing}
						aria-pressed={isEditing}
						className={cn(
							'flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium transition-colors max-md:hidden',
							isEditing
								? 'bg-primary text-primary-foreground'
								: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
						)}
					>
						{isEditing ? (
							'Done'
						) : (
							<>
								<SlidersHorizontal className="size-3.5" />
								Edit
							</>
						)}
					</button>
				</div>
				{isEditing && (
					<p className="border-b px-4 py-2 text-sm text-muted-foreground max-md:shrink-0">
						Untick anything you don't use to hide it from your menu.
					</p>
				)}
				{/* Columns keep every feature in view on desktop; the scroll is only a fallback for short screens */}
				{/* Phones get one scrolling column, and medium screens (landscape
				    phones, iPads) a grid that scrolls down: a height-capped
				    multi-column box would push whole categories off to the side. */}
				<div className="max-h-[75vh] gap-6 overflow-y-auto p-4 md:columns-2 lg:columns-3 max-md:max-h-none max-md:min-h-0 max-md:flex-1 max-md:overscroll-contain max-md:pb-[max(1rem,var(--safe-bottom))] max-md:pt-2 md:max-xl:grid md:max-xl:max-h-none md:max-xl:min-h-0 md:max-xl:grid-cols-2 md:max-xl:items-start md:max-xl:gap-y-0 md:max-xl:overscroll-contain lg:max-xl:grid-cols-3">
					{flatItems.length === 0 && (
						<p className="px-1 py-2 text-sm text-muted-foreground">
							No features found
						</p>
					)}
					{filteredCategories.map((category) => {
						const categoryIds = category.items.map((item) => item.id);
						const allHidden = categoryIds.every((id) => hiddenIds.includes(id));
						return (
							<div key={category.title} className="mb-4 break-inside-avoid max-md:mb-5">
								<div className="mb-1.5 flex items-center justify-between px-1">
									<h4 className="text-xs font-bold text-foreground max-md:text-[13px] max-md:font-semibold max-md:uppercase max-md:tracking-wide max-md:text-muted-foreground">
										{category.title}
									</h4>
									{isEditing && categoryIds.length > 1 && (
										<button
											type="button"
											className="text-xs text-muted-foreground hover:text-foreground max-md:-my-3 max-md:min-h-11 max-md:px-2 max-md:text-[13px] max-md:text-primary"
											onClick={() => handleSetHidden(categoryIds, !allHidden)}
										>
											{allHidden ? 'Show all' : 'Hide all'}
										</button>
									)}
								</div>
								<div className="space-y-0.5">
									{category.items.map((item) =>
										isEditing ? (
											<EditableFeatureRow
												key={item.id}
												item={item}
												isVisible={!hiddenIds.includes(item.id)}
												isHighlighted={
													flatItems[highlightedIndex]?.id === item.id
												}
												onSetVisible={(visible) =>
													handleSetHidden([item.id], !visible)
												}
											/>
										) : (
											<FeatureRow
												key={item.id}
												item={item}
												isPinned={pinnedIds.includes(item.id)}
												isActive={location.pathname.startsWith(item.url)}
												// Phones have no arrow-key highlight to show.
												isHighlighted={
													!isMobile &&
													flatItems[highlightedIndex]?.id === item.id
												}
												decorator={decorators[item.id]}
												onNavigate={handleNavigate}
												onTogglePin={togglePin}
												onHover={() =>
													setHighlightedIndex(
														flatItems.findIndex((i) => i.id === item.id),
													)
												}
											/>
										),
									)}
								</div>
							</div>
						);
					})}
				</div>
			</DialogContent>
		</Dialog>
	);
}

function EditableFeatureRow({
	item,
	isVisible,
	isHighlighted,
	onSetVisible,
}: {
	item: FeatureItem;
	isVisible: boolean;
	isHighlighted: boolean;
	onSetVisible: (visible: boolean) => void;
}) {
	const checkboxId = `feature-visible-${item.id}`;
	return (
		<label
			htmlFor={checkboxId}
			className={cn(
				'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent/50 max-md:min-h-11 max-md:gap-3 max-md:text-[15px] md:max-xl:touch:min-h-11',
				isHighlighted && 'bg-accent text-accent-foreground',
			)}
		>
			<Checkbox
				id={checkboxId}
				checked={isVisible}
				onCheckedChange={(checked) => onSetVisible(checked === true)}
			/>
			<item.icon className="size-3.5 shrink-0 text-muted-foreground max-md:size-5" />
			<span className={cn('truncate', !isVisible && 'text-muted-foreground')}>
				{item.title}
			</span>
		</label>
	);
}

function FeatureRow({
	item,
	isPinned,
	isActive,
	isHighlighted,
	decorator,
	onNavigate,
	onTogglePin,
	onHover,
}: {
	item: FeatureItem;
	isPinned: boolean;
	isActive: boolean;
	isHighlighted: boolean;
	decorator?: FeatureDecorator;
	onNavigate: (url: string) => void;
	onTogglePin: (id: string) => void;
	onHover: () => void;
}) {
	const hasCount = decorator && decorator.count > 0;
	const rowRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isHighlighted) {
			rowRef.current?.scrollIntoView({ block: 'nearest' });
		}
	}, [isHighlighted]);

	return (
		<div
			ref={rowRef}
			className={cn(
				'group flex items-center justify-between rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors max-md:min-h-11 max-md:py-0 max-md:pr-0 max-md:text-[15px] max-md:active:bg-accent md:max-xl:touch:min-h-11 md:max-xl:touch:py-0 md:max-xl:touch:pr-0',
				isActive || isHighlighted
					? 'bg-accent text-accent-foreground'
					: 'hover:bg-accent/50',
			)}
			data-onboarding={item.id === 'campaigns' ? 'campaigns' : undefined}
			onClick={() => onNavigate(item.url)}
			onMouseEnter={onHover}
		>
			<div className="flex items-center gap-2 min-w-0 max-md:gap-3">
				<item.icon className="size-3.5 shrink-0 text-muted-foreground max-md:size-5" />
				<span
					className={cn('truncate', decorator?.hasUnseen && 'font-semibold')}
				>
					{item.title}
				</span>
				{decorator?.hasUnseen && (
					<span className="flex h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
				)}
			</div>
			<div className="flex items-center gap-1 shrink-0">
				{hasCount && (
					<span className="text-xs text-muted-foreground tabular-nums">
						{decorator.count}
					</span>
				)}
				<button
					className={cn(
						'p-0.5 rounded transition-opacity focus-visible:opacity-100 max-md:flex max-md:size-11 max-md:items-center max-md:justify-center md:max-xl:touch:flex md:max-xl:touch:size-11 md:max-xl:touch:items-center md:max-xl:touch:justify-center',
						isPinned
							? 'text-primary opacity-100'
							: 'opacity-0 group-hover:opacity-60 hover:!opacity-100 text-muted-foreground touch:opacity-60 max-md:opacity-60',
					)}
					onClick={(e) => {
						e.stopPropagation();
						onTogglePin(item.id);
					}}
					title={isPinned ? 'Unpin' : 'Pin to sidebar'}
					aria-label={isPinned ? `Unpin ${item.title}` : `Pin ${item.title} to the menu`}
					aria-pressed={isPinned}
				>
					{isPinned ? (
						<PinOff className="size-3 max-md:size-[18px] md:max-xl:touch:size-4" />
					) : (
						<Pin className="size-3 max-md:size-[18px] md:max-xl:touch:size-4" />
					)}
				</button>
			</div>
		</div>
	);
}
